import { formatDateISO } from '@safetyhub/shared';
import {
  getMemberKindLabel,
  getUserIdentifier,
  isOwnerInvitation,
  memberKindOf,
  OrganizationInvitation,
  OrganizationMember,
} from '../../../lib/organizations';
import { isWithdrawnMember } from '../../../lib/memberStatus';
import { isOrganizationMember } from '../../../lib/members';

/**
 * 멤버 목록의 탭·행 파생 — 렌더와 떼어 둔 순수 함수 층.
 *
 * 표가 "가입 멤버"와 "초대"라는 성질이 다른 두 소스를 한 목록으로 합치면서, 어느 탭에
 * 무엇이 들어가는지가 렌더 JSX 안에서 읽히지 않게 됐다. 그 분류 규칙만 여기로 떼어
 * 단위 테스트로 고정한다(`__tests__/membersView.test.ts`).
 */

/** 탭 = 목록 범위의 정본. 배열 순서가 곧 탭 순서이고, `tab`이 카운트 맵의 키와 같다. */
export type MembersTab = 'all' | 'active' | 'invited' | 'inactive';

export const MEMBERS_TABS: { tab: MembersTab; label: string }[] = [
  { tab: 'all', label: '전체 멤버' },
  { tab: 'active', label: '활성' },
  { tab: 'invited', label: '초대 대기' },
  { tab: 'inactive', label: '비활성' },
];

/**
 * 표의 한 행 — 가입 멤버와 초대는 갖는 정보가 달라 union으로 둔다. `kind`로 갈라야
 * 렌더에서 "초대에는 없는 필드"(user·siteAssignments)를 옵셔널 체이닝으로 얼버무리지 않는다.
 */
export type MemberRow =
  | { kind: 'member'; id: string; member: OrganizationMember }
  | { kind: 'invitation'; id: string; invitation: OrganizationInvitation };

export interface MembersSource {
  members: OrganizationMember[];
  invitations: OrganizationInvitation[];
}

/** 최근 활동 필터 — 로그인 적재(`user.lastLoginAt`) 기준. 빈 문자열이면 전체. */
export type ActivityFilter = '' | 'today' | '7d' | '30d' | 'none';

export const ACTIVITY_FILTER_OPTIONS: { value: ActivityFilter; label: string }[] = [
  { value: '', label: '전체 활동' },
  { value: 'today', label: '오늘' },
  { value: '7d', label: '최근 7일' },
  { value: '30d', label: '최근 30일' },
  { value: 'none', label: '기록 없음' },
];

export interface MemberRowFilter {
  /** 검색어. 원문 그대로 넘기면 되고, trim·소문자화는 이 모듈이 한다. */
  query: string;
  /**
   * 역할 필터 — **역할 key**다(P7-b-2). 빈 문자열이면 전체.
   * 멤버 행은 활성 배정 key 중 하나라도 일치하면 남고, 초대 행은 실린 `roleKeys`로 같은 판정을 한다.
   * 옵션 목록은 화면이 지금 로드된 목록에서 뽑는다(`getRoleFilterOptions`) — 12종 시드 + 조직 생성 역할을
   * 손으로 열거할 수 없기 때문이다. 옛 형상(서버가 `roles`를 안 싣던 응답)은 종류 key(`owner`)로 떨어진다.
   */
  roleFilter: string;
  /** 담당 현장 필터(siteId). 빈 문자열이면 전체. */
  siteFilter?: string;
  /** 최근 활동 필터. 빈 문자열이면 전체. */
  activityFilter?: ActivityFilter;
  /** 활동 창 비교 기준 시각 — 테스트가 고정할 수 있게 주입받는다(기본 now). */
  now?: Date;
}

/**
 * 조직 구성원 = 구성원 자격을 주는 역할(`countsAsMember`)을 하나라도 가진 CompanyMember 행 — 서버가 판정해
 * `countsAsMember`로 싣는다(company-rbac P7-b-1). 외부 인원(현장 담당자·외부 결재자)은 `site-staff`만 가져 제외되며
 * 멤버 관리의 대상이 아니다(외부 인원은 결재선·현장 담당자 화면이 관리한다. 2026-08-02 사용자 지적).
 * 이 모듈의 모든 파생(탭 행·카운트·역할 분포·신규 합류)은 이 술어를 지나야 한다.
 */
// 정본은 lib/members로 옮겼다 — 조직 구성원만 대상인 목록이 멤버 관리 밖(현장 점검 담당자 후보
// 등)에도 있어, 이 모듈 안에 두면 그쪽이 규칙을 복사해 쓰다 갈라진다(2026-08-11).
// 기존 소비처가 무변경으로 남도록 여기서 re-export 한다.
export { isOrganizationMember };

/**
 * 그룹 소속을 목록에 표시할지 — 회사 그룹이 2개 이상일 때만이다.
 *
 * 그룹이 하나뿐인 조직은 전원이 그 그룹(본점)이라, 배지가 사람을 구분하지 못하고 자리만
 * 차지한다. 헤더 `GroupSelector`가 선택지 2개 미만이면 스스로 숨는 것과 같은 판단이다.
 * 인자는 회사 그룹 수(`currentMembership.company.groups`) — 목록에 실린 멤버들의 소속을
 * 세면 빈 그룹이 빠져 셀렉터와 답이 갈린다.
 */
export function shouldShowMemberGroups(companyGroupCount: number): boolean {
  return companyGroupCount >= 2;
}

/**
 * 활성 = 계정이 살아 있는 멤버. `user`가 없는 멤버(서명 참가자 등 User 미연동 Member)는
 * 활성도 비활성도 아니다 — 탈퇴 여부를 단정할 근거(`user.deletedAt`)가 없기 때문이다.
 */
export function isActiveMember(member: OrganizationMember): boolean {
  return Boolean(member.user && !member.user.deletedAt);
}

/**
 * 비활성 = 탈퇴한 계정. 근거는 `user.deletedAt` 하나뿐이다(위 주석과 같은 이유).
 * 술어 정본은 `lib/memberStatus`의 `isWithdrawnMember`(순수 술어라 vi.mock 대상인
 * `lib/organizations` 밖에 산다 — 그 모듈 헤더 참조) — 결재 담당자 후보 필터와 같은 것을 쓴다.
 */
export function isInactiveMember(member: OrganizationMember): boolean {
  return isWithdrawnMember(member);
}

/** 검색 대상: 이름 + 로그인 식별자. 이메일 원문은 `getUserIdentifier` 마스킹을 따른다. */
export function matchesMemberQuery(member: OrganizationMember, query: string): boolean {
  const haystack = [
    member.user?.name ?? '',
    member.member?.name ?? '',
    member.user ? getUserIdentifier(member.user.auths) : '',
  ]
    .join(' ')
    .toLowerCase();
  return haystack.includes(query);
}

/** 초대는 아직 이름이 없다 — 초대할 때 넣은 연락 수단이 곧 검색 대상이다. */
export function matchesInvitationQuery(
  invitation: OrganizationInvitation,
  query: string,
): boolean {
  const haystack = [invitation.email ?? '', invitation.phone ?? '', invitation.username ?? '']
    .join(' ')
    .toLowerCase();
  return haystack.includes(query);
}

/**
 * 탭별 행 목록 — 검색·역할 필터가 걸리기 전의 범위다.
 *
 * `all`에 **PENDING 초대만** 넣는 게 이 분류의 핵심이다: 거절·만료된 초대는 조직에 들어올
 * 사람이 아니라 지난 기록이라, 전체 목록에 두면 멤버 수를 부풀린다. 그 기록을 볼 자리는
 * '초대 대기' 탭이고, 그래서 그 탭만 초대 전량(PENDING + 최근 거절/만료)을 받는다.
 */
export function buildMemberRows(source: MembersSource, tab: MembersTab): MemberRow[] {
  // 외부 인원(countsAsMember=false)은 어느 탭에도 서지 않는다 — isOrganizationMember 주석 참조.
  const members = source.members.filter(isOrganizationMember);
  const { invitations } = source;
  const memberRow = (member: OrganizationMember): MemberRow => ({
    kind: 'member',
    id: member.id,
    member,
  });
  // 초대 id와 멤버 id는 다른 테이블에서 오므로 충돌할 이유는 없지만, React key로 쓰이는
  // 값이라 섞이는 순간을 만들지 않는다.
  const invitationRow = (invitation: OrganizationInvitation): MemberRow => ({
    kind: 'invitation',
    id: `invitation-${invitation.id}`,
    invitation,
  });

  switch (tab) {
    case 'all':
      return [
        ...members.map(memberRow),
        ...invitations.filter((i) => i.status === 'PENDING').map(invitationRow),
      ];
    case 'active':
      return members.filter(isActiveMember).map(memberRow);
    case 'invited':
      return invitations.map(invitationRow);
    case 'inactive':
      return members.filter(isInactiveMember).map(memberRow);
  }
}

/**
 * 최근 활동 판정 — 멤버 행만 대상이다. 초대는 아직 로그인할 수 없는 자리라 '기록 없음'에도
 * 넣지 않는다('기록 없음'은 "멤버인데 접속 이력이 없다"라는 물음이고, 초대는 멤버가 아니다).
 */
function matchesActivity(row: MemberRow, filter: ActivityFilter, now: Date): boolean {
  if (!filter) return true;
  if (row.kind !== 'member') return false;
  const lastLoginAt = row.member.user?.lastLoginAt;
  if (filter === 'none') return !lastLoginAt;
  if (!lastLoginAt) return false;
  const loggedAt = new Date(lastLoginAt);
  if (filter === 'today') {
    // 달력 기준 오늘(Asia/Seoul) — 자정 직전 로그인이 "오늘"에서 빠지지 않게 날짜 문자열로 비교.
    return formatDateISO(loggedAt) === formatDateISO(now);
  }
  const windowDays = filter === '7d' ? 7 : 30;
  return now.getTime() - loggedAt.getTime() <= windowDays * 24 * 60 * 60 * 1000;
}

/** 담당 현장 판정 — 멤버는 할당 목록, 초대는 초대에 지정된 현장으로 본다. */
function matchesSite(row: MemberRow, siteId: string): boolean {
  if (!siteId) return true;
  if (row.kind === 'member') {
    return (row.member.siteAssignments ?? []).some((assignment) => assignment.siteId === siteId);
  }
  return row.invitation.siteId === siteId;
}

/**
 * 행의 역할 key 집합 — 필터·분포가 공유하는 한 벌의 판정(P7-b-2).
 *
 * 멤버는 서버가 실은 배정 key, 초대는 초대장에 실린 `roleKeys`다. 둘 다 없으면(옛 형상·역할 없는 초대)
 * 종류 축으로 떨어뜨린다 — 그래야 「소유자」 필터가 옛 응답에서도 답을 내고, 외부 인원은 어느 역할에도 안 걸린다.
 */
function roleKeysOfRow(row: MemberRow): string[] {
  if (row.kind === 'member') {
    const keys = (row.member.roles ?? []).map((r) => r.key);
    if (keys.length > 0) return keys;
    const kind = memberKindOf(row.member);
    return kind ? [kind] : [];
  }
  const keys = row.invitation.roleKeys ?? [];
  if (keys.length > 0) return keys;
  return [isOwnerInvitation(row.invitation) ? 'owner' : 'member'];
}

/** 검색·역할·현장·활동 필터 — 탭 결과 위에 AND로 얹는다(탭이 범위, 필터가 좁히기). */
export function filterMemberRows(rows: MemberRow[], filter: MemberRowFilter): MemberRow[] {
  const q = filter.query.trim().toLowerCase();
  const { roleFilter, siteFilter = '', activityFilter = '' } = filter;
  const now = filter.now ?? new Date();

  return rows.filter((row) => {
    if (roleFilter && !roleKeysOfRow(row).includes(roleFilter)) return false;
    if (!matchesSite(row, siteFilter)) return false;
    if (!matchesActivity(row, activityFilter, now)) return false;
    if (!q) return true;
    return row.kind === 'member'
      ? matchesMemberQuery(row.member, q)
      : matchesInvitationQuery(row.invitation, q);
  });
}

/**
 * 역할 분포 — 시안 `.role-breakdown` 누적바의 데이터.
 *
 * 분모는 가입 멤버 전원이다(초대 제외 — 아직 역할이 확정된 사람이 아니다). 축은 **실제 역할**이다(P7-b-2):
 * 시드만 12종이고 조직이 역할을 더 만들 수 있어 2종 하드코딩으로는 못 담는다. 순서는 서버가 준 순서
 * (`getMembers`가 역할 정의 sortOrder로 정렬해 싣는다)를 **처음 등장 순**으로 따라간다 — 역할 관리 화면과 같은 줄세우기다.
 *
 * ⚠️ **세그 합 ≠ total일 수 있다** — 겸직(한 사람이 역할 둘)이 정상이라 역할별 합이 분모보다 클 수 있다.
 * 화면은 그래서 세그 폭을 total이 아니라 **세그 합**으로 나눠야 100%가 된다(`MembersDesktop`의 바가 그렇게 그린다).
 * 옛 형상(서버가 `roles`를 안 싣던 응답)은 종류 key로 떨어져 종전과 같은 2종 분포가 나온다.
 */
export function getRoleDistribution(
  members: OrganizationMember[],
): { total: number; items: { role: string; label: string; count: number }[] } {
  // 분모도 구성원만 — 외부 인원(countsAsMember=false)을 total에 두면 세그 합이 100%가 안 되는
  // 바(회색 꼬리)가 되고, 그건 "역할 없는 멤버가 있다"는 거짓말이다.
  const organizationMembers = members.filter(isOrganizationMember);
  const counts = new Map<string, { label: string; count: number }>();
  for (const member of organizationMembers) {
    const roles =
      (member.roles ?? []).length > 0
        ? member.roles!
        : (() => {
            const kind = memberKindOf(member);
            return kind ? [{ key: kind, name: getMemberKindLabel(kind) }] : [];
          })();
    for (const role of roles) {
      const hit = counts.get(role.key);
      if (hit) hit.count += 1;
      else counts.set(role.key, { label: role.name, count: 1 });
    }
  }
  return {
    total: organizationMembers.length,
    items: [...counts.entries()].map(([role, { label, count }]) => ({ role, label, count })),
  };
}

/**
 * 역할 필터 옵션 — 지금 목록에 **실제로 등장하는** 역할만 모은다(담당 현장 필터와 같은 자세).
 * 조직의 전체 역할 카탈로그를 따로 불러오지 않는 건, 아무도 안 가진 역할은 골라 봐야 빈 목록만 나오기 때문이다.
 */
export function getRoleFilterOptions(source: MembersSource): { value: string; label: string }[] {
  // 이름은 멤버 행에만 실린다(서버가 배정과 함께 준다) — 먼저 이름 사전을 만든다
  const names = new Map<string, string>();
  for (const member of source.members) {
    for (const role of member.roles ?? []) if (!names.has(role.key)) names.set(role.key, role.name);
  }
  // 그 다음 실제로 등장하는 key를 모은다(초대 행 포함 — 필터가 초대에도 걸리므로).
  // 이름을 모르는 key는 사전에 없으니 종류 라벨로 떨어뜨린다(옛 형상·이름 없는 초대 key).
  const options = new Map<string, string>();
  for (const row of buildMemberRows(source, 'all')) {
    for (const key of roleKeysOfRow(row)) {
      if (options.has(key)) continue;
      options.set(key, names.get(key) ?? getMemberKindLabel(key === 'owner' ? 'owner' : 'member'));
    }
  }
  return [...options.entries()].map(([value, label]) => ({ value, label }));
}

/**
 * 담당 현장 필터 옵션 — 지금 목록에 실제로 등장하는 현장만 모은다(할당·초대 지정 현장의
 * 합집합). 조직의 전체 현장 목록을 따로 불러오지 않는 건, 아무도 없는 현장은 골라 봐야
 * 빈 목록만 나오기 때문이다.
 */
export function getAssignedSiteOptions(source: MembersSource): { id: string; name: string }[] {
  const byId = new Map<string, string>();
  for (const member of source.members) {
    for (const assignment of member.siteAssignments ?? []) {
      if (assignment.site?.name) byId.set(assignment.siteId, assignment.site.name);
    }
  }
  for (const invitation of source.invitations) {
    if (invitation.siteId && invitation.site?.name) byId.set(invitation.siteId, invitation.site.name);
  }
  return [...byId.entries()]
    .map(([id, name]) => ({ id, name }))
    .sort((a, b) => a.name.localeCompare(b.name, 'ko'));
}

/**
 * 탭 배지 수 — **검색·역할 필터와 무관한 전체 기준**이다(결재함 `tabCounts`와 같은 규약).
 * 필터를 반영하면 "다른 탭에 몇 건 있나"라는 배지 본래의 물음에 답하지 못한다.
 */
export function getMembersTabCounts(source: MembersSource): Record<MembersTab, number> {
  return {
    all: buildMemberRows(source, 'all').length,
    active: buildMemberRows(source, 'active').length,
    invited: buildMemberRows(source, 'invited').length,
    inactive: buildMemberRows(source, 'inactive').length,
  };
}

/**
 * 아직 수락을 기다리는 초대 수 — '초대 대기' **카드**의 값이다.
 *
 * 같은 이름의 탭 배지(거절·만료 포함)와 값이 갈릴 수 있는데, 그게 맞다: 카드는 "지금
 * 답을 기다리는 건"이라 조치할 대상이 곧 값이고, 탭은 최근 초대 기록을 훑는 자리다.
 */
export function getPendingInvitationCount(invitations: OrganizationInvitation[]): number {
  return invitations.filter((i) => i.status === 'PENDING').length;
}

/** '초대 대기' 카드의 만료 임박 경고 창(시간) — 24시간 안에 만료될 초대. */
export const INVITATION_EXPIRING_HOURS = 24;

/**
 * 만료 임박 초대 수 — '초대 대기' 카드의 경고 pill(시안 `만료 임박 1`).
 * PENDING이면서 만료가 [now, now+24h] 안인 것만 — 이미 지난 초대는 상태가 곧 EXPIRED로
 * 돌 것이라 "임박"이 아니고, 24시간 밖은 아직 조치를 재촉할 이유가 없다.
 */
export function countExpiringInvitations(
  invitations: OrganizationInvitation[],
  now: Date = new Date(),
): number {
  const limit = now.getTime() + INVITATION_EXPIRING_HOURS * 60 * 60 * 1000;
  return invitations.filter((invitation) => {
    if (invitation.status !== 'PENDING') return false;
    const expiresAt = new Date(invitation.expiresAt).getTime();
    return expiresAt > now.getTime() && expiresAt <= limit;
  }).length;
}

/**
 * 이번 달 합류한 멤버 수 — '전체 멤버' 카드의 보조 문구용.
 * 월 비교를 현지 기준으로 하려고 `formatDateISO`(Asia/Seoul 변환)의 `YYYY-MM`을 쓴다 —
 * `Date.getMonth()`는 실행 환경 시간대를 타서 월말·월초 하루가 어긋난다.
 */
export function countJoinedThisMonth(members: OrganizationMember[], now: Date): number {
  const thisMonth = formatDateISO(now).slice(0, 7);
  return members
    .filter(isOrganizationMember) // 외부 contact 연결은 "합류"가 아니다
    .filter((member) => formatDateISO(new Date(member.createdAt)).slice(0, 7) === thisMonth)
    .length;
}

/**
 * 목록이 비었을 때의 문구 — 범위마다 "없다"의 뜻이 달라 탭별로 갈라 쓴다.
 *
 * ⚠️ 화면은 이 맵을 직접 읽지 말고 `getMembersEmptyCopy(tab, access)`를 쓴다 — '초대 대기' 탭의 빈
 * 목록은 「없다」와 「볼 수 없다」 **두 뜻**이고, 이 맵은 앞의 뜻만 담는다(아래 `InvitationsAccess`).
 */
export const MEMBERS_EMPTY_COPY: Record<MembersTab, string> = {
  all: '조건에 맞는 멤버가 없습니다.',
  active: '조건에 맞는 멤버가 없습니다.',
  invited: '대기 중인 초대가 없습니다.',
  inactive: '탈퇴한 멤버가 없습니다.',
};

/**
 * 초대 칸(카드·탭 배지·목록)이 지금 무엇을 말할 수 있나 — **「없다」와 「볼 수 없다」는 다른 상태다.**
 *
 * 초대 목록은 `member.invite`가 있을 때만 싣는다(`useMembersState.loadData` — 서버
 * `GET :organizationId/invitations`에 그 키가 붙어 있어 무조건 부르면 403이 `Promise.all`을 깨
 * **멤버 목록까지** 날린다). 그래서 키가 없는 사람의 `invitations`는 **빈 것이 아니라 묻지 않은 것**이다.
 * 그 0을 「대기 중인 초대가 없습니다」·카드 값 `0`으로 **서술**하면 화면이 거짓말을 하고, 그 사람은
 * 이미 보낸 초대를 못 본 채 **재초대해 중복 초대**를 만든다.
 *
 * 숨기지 않고 **말해 주는** 쪽을 고른 근거: `member.invite`는 `orgAdjustable=true`(조직이 어떤
 * 역할에든 줄 수 있는 키 — `organizations.service.ts` P7-g A 주석)라 이 막힘은 **풀릴 수 있다**.
 * 숨기면 사용자는 기능의 존재조차 몰라 관리자에게 요청할 수도 없다.
 *
 * 판정을 이 순수 함수 하나로 둬 데스크톱·모바일이 **같은 자**를 쓴다(각자 판정하면 같은 상태에
 * 두 화면이 다르게 반응한다).
 *
 * ⚠️ '전체 멤버' 탭은 이 축 **밖**이다 — 그 탭은 PENDING 초대를 멤버와 같은 표에 얹으므로 키가 없으면
 * 초대 줄만큼 적게 보이지만, **보이는 줄 수와 배지가 일치**하고 「초대가 없다」고 말하지도 않는다.
 * 거기까지 `—`로 두면 키 없이도 전량 오는 **멤버 수**를 모르는 값으로 만들어 더 나빠진다.
 */
export type InvitationsAccess = 'visible' | 'forbidden';

export function getInvitationsAccess(canInviteMembers: boolean): InvitationsAccess {
  return canInviteMembers ? 'visible' : 'forbidden';
}

/**
 * 초대 칸이 지금 그릴 것 — **세 상태**다. 이 결함의 본질이 앞의 둘을 한 불리언으로 합친 것이었으므로
 * (`invitations.length === 0`이 「0건」과 「묻지 않았다」를 함께 뜻했다) 상태를 이름으로 갈라 둔다.
 *
 * | 상태 | 뜻 | 화면 |
 * |---|---|---|
 * | `listed` | 볼 수 있고 N건 | 목록·수를 그대로 그린다 |
 * | `empty` | 볼 수 있고 **실제로** 0건 | 「대기 중인 초대가 없습니다」 — 사실이다 |
 * | `forbidden` | 볼 수 없음(키 없음) | 「볼 권한이 없습니다」 — 0은 사실이 아니다 |
 *
 * ⚠️ `count`를 **인자로 받는다**(함수가 스스로 세지 않는다) — 이 화면의 「몇 건인가」는 자리마다 다른
 * 질문이다: '초대 대기' **카드**는 PENDING만, **탭 배지**와 모바일 섹션은 초대 전량(최근 거절·만료
 * 포함)이다. 세는 축을 함수 안에 숨기면 `empty`가 **어느 0**인지 알 수 없어지고, 두 자리가 같은
 * 상태값을 다른 뜻으로 읽게 된다.
 */
export type InvitationsDisplayState = 'listed' | 'empty' | 'forbidden';

export function getInvitationsDisplayState(
  access: InvitationsAccess,
  count: number,
): InvitationsDisplayState {
  if (access === 'forbidden') return 'forbidden';
  return count > 0 ? 'listed' : 'empty';
}

/** 초대 칸을 볼 수 없을 때의 안내 — 데스크톱·모바일이 같은 문구를 쓴다. */
export const INVITATIONS_FORBIDDEN_COPY =
  '초대 현황을 볼 권한이 없습니다. 조직 관리자에게 「멤버 초대」 권한을 요청하세요.';

/**
 * 셀 수 없을 때 카드·탭 배지에 넣는 값 — `0`은 사실이 아니라 수를 쓰지 않는다.
 * 같은 화면 '평균 활동도' 카드가 값 없음을 `—`로 두는 것과 같은 자리.
 */
export const INVITATIONS_UNCOUNTABLE_VALUE = '—';

/**
 * 빈 목록 문구 — 범위(탭)와 **볼 수 있나**를 함께 본다. 목록이 0줄일 때만 불리므로 위 세 상태 중
 * `empty`(→ 탭별 「없다」)와 `forbidden`(→ 「볼 수 없다」)을 가르는 자리다.
 *
 * '초대 대기' 탭만 두 뜻으로 갈린다: 다른 탭의 원천은 멤버 목록이고 그건 키 없이도 전량 오므로
 * 「없다」가 사실이다.
 */
export function getMembersEmptyCopy(tab: MembersTab, access: InvitationsAccess): string {
  if (tab === 'invited' && access === 'forbidden') return INVITATIONS_FORBIDDEN_COPY;
  return MEMBERS_EMPTY_COPY[tab];
}
